/**
 * Public contact retrieval page (§10).
 *
 * The one journey that must work under stress, for a frightened child, on
 * someone else's phone. Properties this page must honour:
 *
 *   - **Failures are uniform.** One generic message; never says which value was
 *     wrong. The only distinction is "we could not reach KinCode", which is a
 *     transport fact the visitor can already observe and tells an attacker
 *     nothing.
 *   - **Nothing sensitive persists.** Credentials and contacts live in local
 *     variables and the DOM, never in `localStorage`, `sessionStorage` or
 *     IndexedDB. A refresh loses them, which is intended: the contacts must not
 *     come back for the next person holding the phone.
 *   - **The PIN never enters the URL.** The *phrase* may, and only in one
 *     direction: a scanned card hands it to this page in a fragment, which is
 *     read, put into the field and erased from the address bar before anything
 *     else happens. This is a deliberate, recorded exception to "credentials
 *     never go in the URL" — the phrase alone retrieves nothing, and it is
 *     already printed in plain sight on the card that carried it. See
 *     `src/ui/phrase.js`. Nothing is looked up until a PIN is typed.
 *   - **Contacts leave the DOM when hidden**, not merely `display: none`. Hidden
 *     means removed.
 *   - The emergency notice is always present (§14).
 */

import { el, icon, fieldValue } from "../ui/dom.js";
import { pageHeader, emergencyNotice, errorAlert } from "../ui/patterns.js";
import { renderContactList } from "../ui/contact-list.js";
import { phraseFromLocation } from "../ui/phrase.js";

/** The single generic failure message. Must not vary by cause. */
const GENERIC_FAILURE =
  "Those details could not be verified. Check the phrase and PIN and try again.";

/** Shown only when the request never reached the server. */
const UNREACHABLE =
  "We could not reach KinCode. Check your connection and try again — your details were not wrong.";

/**
 * How long contacts stay on screen before hiding themselves.
 *
 * Long enough to read four numbers aloud, write one down, or dial under stress;
 * short enough that a phone put down on a table does not leave a family's
 * contacts showing. Ninety seconds is a judgement, not a derived figure.
 */
const AUTO_HIDE_SECONDS = 90;

/**
 * The phrase handed over by a scanned card, captured once per page load.
 *
 * `null` means "not looked at yet"; a string means it has been read and the
 * address bar cleaned, and that string is reused for the rest of the visit.
 *
 * The caching is load-bearing, not an optimisation. The router re-renders a route
 * whenever the auth state changes, and against a **real** Firebase backend the
 * initial session resolves asynchronously — so `/retrieve` renders once at boot
 * and again a moment later when `onAuthStateChanged` first fires. Reading the URL
 * on each render meant the second one found an address bar the first had already
 * stripped and rebuilt the field empty. The mock backend is ready synchronously
 * and renders once, which is why every test passed while production did not.
 *
 * Module scope rather than `sessionStorage`: a refresh must still lose it.
 *
 * @type {string | null}
 */
let scannedPhrase = null;

/**
 * Read the scanned phrase and clear it from the address bar, once.
 *
 * Junk is discarded silently — no message names the problem, because a bad value
 * is far more likely a scanning artefact than a person's mistake, and an error
 * distinguishing "wrong shape" from "wrong phrase" would hand an attacker exactly
 * the oracle the server's loose validation avoids.
 *
 * @returns {string}
 */
function takeScannedPhrase() {
  if (scannedPhrase === null) {
    scannedPhrase = phraseFromLocation(window.location);
    // Cleared before anything renders, so the phrase is never left in the URL for
    // a shoulder, a screenshot or a shared link to pick up.
    if (window.location.hash || window.location.search) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }
  return scannedPhrase;
}

/** @type {import("../app/router.js").PageRenderer} */
export default function renderRetrieval(context) {
  const frag = document.createDocumentFragment();
  frag.append(pageHeader("Retrieve contacts"));
  frag.append(emergencyNotice());

  // A scanned card hands the phrase over here. Survives the re-render that a real
  // auth backend triggers a moment after boot — see `takeScannedPhrase`.
  const prefilledPhrase = takeScannedPhrase();

  // Results and errors are announced here. `polite` rather than `assertive`: the
  // visitor has just pressed a button and is already attending to the outcome.
  const resultRegion = el("div", { class: "kin-stack", "aria-live": "polite" });

  // `ReturnType` rather than `number`: the DOM and Node typings disagree about
  // what a timer handle is, and this file is checked against both.
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let hideTimer;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  let countdownTimer;

  const phraseField = el("wa-input", {
    label: "Recovery phrase",
    hint: "The four words you were given, for example otter-lantern-meadow-compass.",
    name: "phrase",
    value: prefilledPhrase,
    autocomplete: "off",
    autocapitalize: "off",
    autocorrect: "off",
    spellcheck: "false",
    required: true,
  });

  /*
   * Focus is deliberately NOT moved to the PIN field when the phrase arrives
   * prefilled. It would save one keypress and cost a screen-reader user the
   * heading and the 999 notice, which they would be dropped straight past — on
   * the one journey that has to work for someone frightened and unfamiliar. The
   * note below does the same job without hijacking anyone's position.
   */
  const prefillNote = prefilledPhrase
    ? el("p", { class: "kin-note" }, [
        "The recovery phrase from your card has been filled in. Enter the PIN — " +
          "it is not on the card, and was given to you separately.",
      ])
    : null;

  const pinField = el("wa-input", {
    label: "PIN",
    hint: "The six digits you were given.",
    name: "pin",
    // Not type="number": that would strip leading zeroes, offer spinners, and
    // let a stray scroll change the value (§10).
    type: "password",
    inputmode: "numeric",
    autocomplete: "off",
    "password-toggle": true,
    required: true,
  });

  const submit = el("wa-button", { type: "submit", variant: "brand" }, [
    icon("key"),
    "Find contacts",
  ]);

  /**
   * Remove every trace of a retrieval from the page and from memory.
   *
   * Called by the Hide button, by the auto-hide timer, and after a failure.
   * Emptying `resultRegion` removes the contact nodes from the document
   * entirely — the acceptance criterion is removal, not concealment.
   *
   * @param {string} [announcement] Replacement text for the live region.
   */
  function clearResults(announcement) {
    globalThis.clearTimeout(hideTimer);
    globalThis.clearInterval(countdownTimer);
    hideTimer = undefined;
    countdownTimer = undefined;
    resultRegion.replaceChildren();
    if (announcement) {
      resultRegion.append(el("p", { class: "kin-note" }, [announcement]));
    }
  }

  /**
   * Hide contacts and return focus somewhere sensible.
   *
   * Focus goes back to the phrase field: the element that starts the journey, so
   * a keyboard or screen-reader user is not left on a button that no longer
   * exists. Losing focus to `document.body` would drop a screen-reader user to
   * the top of the page with no announcement of what happened.
   */
  function hideContacts() {
    clearResults("Contacts hidden. Enter the phrase and PIN again if you still need them.");
    /** @type {HTMLElement} */ (phraseField).focus?.();
  }

  /**
   * Render a successful retrieval.
   *
   * @param {import("../types/models.js").Contact[]} contacts
   */
  function showContacts(contacts) {
    const remaining = el("span", { class: "kin-countdown" }, [`${AUTO_HIDE_SECONDS}s`]);

    const hideButton = el(
      "wa-button",
      { variant: "brand", appearance: "outlined", on: { click: hideContacts } },
      [icon("eye-slash"), "Hide contacts"],
    );

    resultRegion.replaceChildren(
      el("h2", {}, ["Contacts found"]),
      renderContactList(contacts),
      el("div", { class: "kin-cta-row" }, [hideButton]),
      el("p", { class: "kin-note" }, ["These will hide automatically in ", remaining, "."]),
      el("p", { class: "kin-note" }, [
        "If you call from a phone that is not yours, the number may stay in its " +
          "call history. KinCode cannot remove it.",
      ]),
    );

    let secondsLeft = AUTO_HIDE_SECONDS;
    countdownTimer = globalThis.setInterval(() => {
      secondsLeft -= 1;
      // The router replaces page content on navigation without telling us, so
      // check we are still on screen rather than counting down into nothing.
      if (!resultRegion.isConnected) {
        clearResults();
        return;
      }
      remaining.textContent = `${Math.max(secondsLeft, 0)}s`;
    }, 1000);

    hideTimer = globalThis.setTimeout(() => {
      hideContacts();
    }, AUTO_HIDE_SECONDS * 1000);
  }

  const form = el(
    "form",
    {
      class: "kin-stack kin-form",
      // novalidate: we present our own plain-language errors rather than the
      // browser's, and keep the failure message uniform.
      novalidate: true,
      on: {
        submit: async (event) => {
          event.preventDefault();
          clearResults();
          submit.setAttribute("loading", "");

          // The scan is spent. Without this it would refill the field on any
          // later render of this page — after a successful retrieval deliberately
          // blanked it, or on a return visit during the same page load.
          scannedPhrase = "";

          const phrase = fieldValue(phraseField);
          const pin = fieldValue(pinField);

          let result;
          try {
            result = await context.services.retrieval.retrieve(phrase, pin);
          } finally {
            submit.removeAttribute("loading");
          }

          if (!result.ok) {
            resultRegion.replaceChildren(
              errorAlert(result.unreachable ? UNREACHABLE : GENERIC_FAILURE),
            );
            return;
          }

          // Clear the credentials from the form on success. On a borrowed phone
          // the next person must not find the phrase and PIN sitting in the
          // fields, and the visitor no longer needs them.
          setFieldValue(phraseField, "");
          setFieldValue(pinField, "");

          showContacts(result.contacts);
        },
      },
    },
    [phraseField, prefillNote, pinField, submit],
  );

  frag.append(form, resultRegion);
  return frag;
}

/**
 * Clear a Web Awesome input.
 *
 * @param {Element} field
 * @param {string} value
 */
function setFieldValue(field, value) {
  /** @type {{ value?: string }} */ (field).value = value;
}
